<script setup lang="ts">
import { useI18n } from "vue-i18n";
import type { ReportResult } from "../services/types";
import type { AppError } from "../services/errors";
import type { DeletionState } from "../composables/useReportFlow";
import AppIcon from "./AppIcon.vue";
import ErrorNotice from "./ErrorNotice.vue";
import LoadingSpinner from "./LoadingSpinner.vue";

defineProps<{ result: ReportResult; deletion: DeletionState; error: AppError | null }>();
defineEmits<{ retryDeletion: [] }>();
const { t } = useI18n();
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="card flex flex-col items-center text-center" role="status">
      <div
        class="rounded-full bg-gradient-to-br p-4 text-white shadow-lg ring-3 ring-white"
        :class="
          result.trainingExercise
            ? 'from-emerald-500 to-emerald-600'
            : 'from-indigo-400 to-indigo-500'
        "
      >
        <AppIcon name="shield" class="size-12" />
      </div>
      <h1 class="mt-4 text-xl font-bold">
        {{ t(result.trainingExercise ? "good_job_test" : "report_submitted") }}
      </h1>
      <p v-if="result.trainingExercise" class="mt-2 font-medium text-slate-500 italic">
        {{ t("points_earned") }}
      </p>
    </div>
    <div
      v-if="deletion === 'moving' || deletion === 'moved'"
      class="card flex items-center justify-center gap-2 text-sm font-medium text-slate-500"
      role="status"
    >
      <LoadingSpinner v-if="deletion === 'moving'" class="size-4" />
      <AppIcon v-else name="check" class="size-5 shrink-0 text-emerald-500" />
      {{ t(deletion === "moving" ? "now_deleting_email" : "email_deleted_success") }}
    </div>
    <div v-if="deletion === 'manual' && error" class="space-y-3">
      <p class="text-sm text-slate-600">{{ t("manual_deletion") }}</p>
      <ErrorNotice
        :error="error"
        :action-label="t(error.messageKey === 'error_auth' ? 'login' : 'retry_deletion')"
        @retry="$emit('retryDeletion')"
      />
    </div>
  </section>
</template>
